import React, { useState } from "react";
import styled, { css } from "styled-components";
import { Theme } from "../../Theme";
import UsageRateTableTooltip from "../tooltip/UsageRateTableTooltip";

const blockStyles = {
    0: css`
        background-color: ${Theme.color.neutral100};
        &:hover {
            background-color: ${Theme.color.gray200};
        }
    `,
    1: css`
        background-color: ${Theme.color.violet200};
        &:hover {
            background-color: ${Theme.color.violet300};
        }
    `,
    2: css`
        background-color: ${Theme.color.violet400};
        &:hover {
            background-color: ${Theme.color.violet450};
        }
    `,
    3: css`
        background-color: ${Theme.color.indigo700};
        &:hover {
            background-color: ${Theme.color.indigo800};
        }
    `,
};

// 1. 전체 그리드 컨테이너
export const GridContainer = styled.div`
    display: grid;
    grid-template-columns: auto repeat(7, 1fr);
    grid-template-rows: auto repeat(12, 1fr);
    gap: 8px; /* 셀 사이의 간격 */
    padding: 16px;
    background-color: #fff;
    border-radius: 12px;
    width: 100%;
    box-sizing: border-box;
    height: 100%;
    margin: auto;
`;

// 2. 요일 헤더 셀 (Sun, Mon, ...)
export const HeaderCell = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${(props) => props.fontSize || Theme.text.c5.fontSize};
    font-family: ${(props) => props.fontFamily || Theme.text.default.fontFamily};
    letter-spacing: ${(props) => props.letterSpacing || Theme.text.default.letterSpacing};
    line-height: ${(props) => props.lineHeight || Theme.text.default.lineHeight};
    font-weight: ${(props) => props.fontWeight || Theme.text.c5.fontWeight};
    color: #6a7581;
    padding-bottom: 8px;
`;

// 3. 시간 레이블 셀 (12 am, 2 am, ...)
export const TimeLabelCell = styled.div`
    display: flex;
    text-align: right;
    align-items: center;
    justify-content: center;
    padding-right: 10px;
    font-size: ${(props) => props.fontSize || Theme.text.c5.fontSize};
    font-family: ${(props) => props.fontFamily || Theme.text.default.fontFamily};
    letter-spacing: ${(props) => props.letterSpacing || Theme.text.default.letterSpacing};
    line-height: ${(props) => props.lineHeight || Theme.text.default.lineHeight};
    font-weight: ${(props) => props.fontWeight || Theme.text.c5.fontWeight};
    color: #6a7581;
`;

// 4. 실제 데이터 블록
export const GridBlock = styled.div`
    border-radius: 10px;
    transition: all 0.2s ease;
    cursor: pointer;

    ${(props) => blockStyles[props.level || 0]}
`;

const TooltipWrapper = styled.div`
    position: absolute;
    z-index: 100;
    left: ${(props) => props.x}px;
    top: ${(props) => props.y}px;
    pointer-events: none;
`;

const RelativeContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`;

// 캘린더에 필요한 상수 데이터
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// 12개의 시간 슬롯 (12am ~ 10pm)
const TIMES = ["12 am", "2 am", "4 am", "6 am", "8 am", "10 am", "12 pm", "2 pm", "4 pm", "6 pm", "8 pm", "10 pm"];

const dataSimple = [
    // S  M  T  W  T  F  S
    [0, 0, 0, 0, 0, 0, 0], // 12am
    [0, 1, 0, 0, 0, 0, 0], // 2am
    [0, 0, 0, 0, 0, 2, 0], // 4am
    [0, 1, 1, 0, 0, 3, 0], // 6am
    [0, 1, 1, 0, 0, 1, 1], // 8am
    [1, 3, 1, 1, 1, 1, 1], // 10am
    [1, 1, 1, 1, 1, 1, 3], // 12pm
    [2, 2, 3, 2, 3, 2, 2], // 2pm
    [3, 3, 3, 1, 3, 3, 3], // 4pm
    [1, 2, 1, 1, 1, 2, 3], // 6pm
    [1, 2, 1, 3, 1, 2, 3], // 8pm
    [0, 1, 0, 2, 0, 2, 0], // 10pm
];

const UsageRateTable = () => {
    const [tooltip, setTooltip] = useState({
        show: false,
        x: 0,
        y: 0,
        data: null,
    });

    const handleMouseEnter = (e, level, dayIndex, timeIndex) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const containerRect = e.currentTarget.closest(".grid-container").getBoundingClientRect();

        // 5번째 열(index 4)부터는 왼쪽에 표시, 그 외에는 오른쪽에 표시
        const isLeft = dayIndex >= 4;
        const tooltipWidth = 186 + 48; // CardContainer width(186) + padding(24*2) = 234

        let x = rect.right - containerRect.left + 8;
        if (isLeft) {
            x = rect.left - containerRect.left - tooltipWidth - 8;
        }

        // 8번째 행(index 7)부터는 위쪽에 표시되도록 y 좌표 조정
        const isBottom = timeIndex >= 7;
        const tooltipHeight = 150; // 대략적인 툴팁 높이 (패딩 48 + 텍스트 영역 + 통계 영역)

        let y = rect.top - containerRect.top - 10;
        if (isBottom) {
            y = rect.bottom - containerRect.top - tooltipHeight + 10;
        }

        setTooltip({
            show: true,
            x: x,
            y: y,
            data: { level, dayIndex, timeIndex },
        });
    };

    const handleMouseLeave = () => {
        setTooltip((prev) => ({ ...prev, show: false }));
    };

    const handleClick = (e, level, dayIndex, timeIndex) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const containerRect = e.currentTarget.closest(".grid-container").getBoundingClientRect();

        const isLeft = dayIndex >= 4;
        const tooltipWidth = 186 + 48;

        let x = rect.right - containerRect.left + 8;
        if (isLeft) {
            x = rect.left - containerRect.left - tooltipWidth - 8;
        }

        const isBottom = timeIndex >= 7;
        const tooltipHeight = 150;

        let y = rect.top - containerRect.top - 10;
        if (isBottom) {
            y = rect.bottom - containerRect.top - tooltipHeight + 10;
        }

        setTooltip({
            show: true,
            x: x,
            y: y,
            data: { level, dayIndex, timeIndex },
        });
    };

    return (
        <RelativeContainer>
            <GridContainer className="grid-container">
                <HeaderCell />
                {DAYS.map((day) => (
                    <HeaderCell key={day}>{day}</HeaderCell>
                ))}

                {dataSimple.map((row, timeIndex) => (
                    <React.Fragment key={timeIndex}>
                        <TimeLabelCell>{TIMES[timeIndex]}</TimeLabelCell>

                        {row.map((level, dayIndex) => (
                            <GridBlock
                                key={`${dayIndex}-${timeIndex}`}
                                level={level}
                                onMouseEnter={(e) => handleMouseEnter(e, level, dayIndex, timeIndex)}
                                onMouseLeave={handleMouseLeave}
                                onClick={(e) => handleClick(e, level, dayIndex, timeIndex)}
                            />
                        ))}
                    </React.Fragment>
                ))}
            </GridContainer>

            {tooltip.show && (
                <TooltipWrapper x={tooltip.x} y={tooltip.y}>
                    <UsageRateTableTooltip data={tooltip.data} />
                </TooltipWrapper>
            )}
        </RelativeContainer>
    );
};

export default UsageRateTable;
